import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Clock, ExternalLink, FileText, Loader2, Mail, Upload } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import type { InternalRequestNodeData } from "@/lib/productGraph";
import {
  getFilteredProducts,
  formatCatalogAmountMinor,
  catalogCustomerAmountMinor,
} from "@/services/certificatePricingCatalogService";
import {
  getEnterpriseRequestParticipants,
  uploadEnterpriseRequestDocument,
  type EnterpriseRequestParticipant,
} from "@/services/enterpriseCatalogService";
import { useAuth } from "@/contexts/AuthContext";

interface InternalRequestNodeViewProps {
  nodeData: InternalRequestNodeData;
  enterpriseRequestId: string;
  nodeId: string;
  childRequestIds: Record<string, string>;
  actorLabel?: string;
  allPayloadValues?: Record<string, unknown>;
}

export function InternalRequestNodeView({
  nodeData,
  enterpriseRequestId,
  nodeId,
  childRequestIds,
  actorLabel = 'Participante',
  allPayloadValues = {},
}: InternalRequestNodeViewProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingActorIdx, setUploadingActorIdx] = useState<number | null>(null);
  const multiFileInputRef = useRef<HTMLInputElement>(null);
  const pendingActorIdxRef = useRef<number | null>(null);

  const participantsQuery = useQuery({
    queryKey: ["enterprise-request-participants", enterpriseRequestId],
    queryFn: () => getEnterpriseRequestParticipants(enterpriseRequestId),
  });

  const participants = participantsQuery.data ?? [];
  const isMultiActor = participants.length > 1;

  // Single-actor linked child (only used when not multi-actor)
  const linkedChildId = childRequestIds[nodeId];

  const productsQuery = useQuery({
    queryKey: ["internalRequestProducts", nodeData.productFilter],
    queryFn: () => getFilteredProducts(nodeData.productFilter),
    enabled: isMultiActor ? true : !linkedChildId,
  });

  function navigateToRequest(targetType: string): void {
    const params = new URLSearchParams({
      requestType: targetType,
      returnEnterpriseRequest: enterpriseRequestId,
      returnStep: nodeId,
    });
    navigate(`/requests/new?${params.toString()}`);
  }

  // State M: Multi-actor — render per-participant cards
  if (isMultiActor) {
    async function handleMultiActorUpload(file: File, actorIdx: number): Promise<void> {
      const actorKey = `${nodeId}_${actorIdx}`;
      setUploadingActorIdx(actorIdx);
      try {
        await uploadEnterpriseRequestDocument(enterpriseRequestId, actorKey, file);
        await queryClient.invalidateQueries({ queryKey: ["me", "enterprise-requests"] });
        toast.success(t("requests.user.internalRequest.uploadSuccess"));
      } catch {
        toast.error(t("requests.user.internalRequest.uploadError"));
      } finally {
        setUploadingActorIdx(null);
        if (multiFileInputRef.current) multiFileInputRef.current.value = "";
      }
    }

    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{nodeData.label}</CardTitle>
          {nodeData.description && (
            <p className="text-sm text-muted-foreground">{nodeData.description}</p>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          <input
            ref={multiFileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.webp"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              const idx = pendingActorIdxRef.current;
              if (!file || idx === null) return;
              void handleMultiActorUpload(file, idx);
            }}
          />
          {participants.map((participant: EnterpriseRequestParticipant, idx: number) => {
            const actorKey = `${nodeId}_${idx}`;
            const actorLinkedId = childRequestIds[actorKey] ?? participant.childRequestId;
            const actorPayload = allPayloadValues[actorKey] as Record<string, unknown> | undefined;
            const hasManualUpload = actorPayload?.fileUrl != null;
            const isCurrentUser = participant.userId === user?.id;
            const displayName = participant.user?.firstName
              ? `${participant.user.firstName} ${participant.user.lastName ?? ""}`.trim()
              : participant.email;
            const isUploadingThis = uploadingActorIdx === idx;

            return (
              <div
                key={participant.id}
                className="flex items-center justify-between rounded-lg border px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {`${actorLabel} ${idx + 1}`}
                    </span>
                    {isCurrentUser && (
                      <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                        {t("requests.user.internalRequest.you")}
                      </span>
                    )}
                  </div>
                  <p className="truncate text-sm font-medium text-foreground">{displayName}</p>
                </div>

                <div className="ml-3 shrink-0">
                  {participant.status === "pending" ? (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Mail className="h-3.5 w-3.5" />
                      <span>{t("requests.user.internalRequest.invitePending")}</span>
                    </div>
                  ) : actorLinkedId ? (
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs"
                        onClick={() => navigate(`/requests/${actorLinkedId}?source=notary`)}
                      >
                        {t("requests.user.internalRequest.viewCertificate")}
                      </Button>
                    </div>
                  ) : hasManualUpload ? (
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      <span className="text-xs font-medium text-green-700">
                        {t("requests.user.internalRequest.uploaded")}
                      </span>
                    </div>
                  ) : participant.status === "declined" ? (
                    <span className="text-xs text-destructive">
                      {t("requests.user.internalRequest.declined")}
                    </span>
                  ) : isCurrentUser ? (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={isUploadingThis}
                        onClick={() => {
                          pendingActorIdxRef.current = idx;
                          multiFileInputRef.current?.click();
                        }}
                      >
                        {isUploadingThis ? (
                          <>
                            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                            {t("requests.user.internalRequest.uploading")}
                          </>
                        ) : (
                          <>
                            <Upload className="mr-1.5 h-3.5 w-3.5" />
                            {t("requests.user.internalRequest.uploadFile")}
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          if (productsQuery.data && productsQuery.data.length > 0) {
                            navigateToRequest(productsQuery.data[0]!.intent.slug);
                          }
                        }}
                        disabled={!productsQuery.data?.length || isUploadingThis}
                      >
                        {t("requests.user.internalRequest.request")}
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Clock className="h-3.5 w-3.5" />
                      <span>{t("requests.user.internalRequest.waiting")}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    );
  }

  // State A: Child already linked (single-actor)
  if (linkedChildId) {
    return (
      <Card>
        <CardContent className="space-y-3 pt-4">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            <span className="text-sm font-medium text-foreground">
              {t("requests.user.internalRequestChildLinked")}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            {t("requests.user.internalRequestWaiting")}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/requests/${linkedChildId}`)}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            {t("requests.user.internalRequestViewChild")}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Loading state
  if (productsQuery.isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (productsQuery.isError) {
    return (
      <Card>
        <CardContent className="py-6 text-center">
          <p className="text-sm text-destructive">
            {t("requests.user.internalRequestLoadError")}
          </p>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => productsQuery.refetch()}
          >
            {t("common.retry")}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const products = productsQuery.data ?? [];

  // State B: Single product — direct CTA
  if (products.length === 1) {
    const { intent, catalogItem } = products[0]!;
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{nodeData.label}</CardTitle>
          {nodeData.description && (
            <p className="text-sm text-muted-foreground">
              {nodeData.description}
            </p>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-lg border px-4 py-3">
            <div className="flex items-center gap-3 min-w-0">
              <FileText className="h-5 w-5 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-sm font-medium">{catalogItem?.displayName ?? intent.slug}</p>
                {catalogItem?.description && (
                  <p className="text-xs text-muted-foreground">
                    {catalogItem.description}
                  </p>
                )}
              </div>
            </div>
            <Button size="sm" className="ml-3 shrink-0" onClick={() => navigateToRequest(intent.slug)}>
              {t("requests.user.internalRequestStart")}
            </Button>
          </div>

          <div className="relative flex items-center">
            <div className="flex-1 border-t" />
            <span className="px-3 text-xs text-muted-foreground">o</span>
            <div className="flex-1 border-t" />
          </div>

          <div className="flex items-center justify-between rounded-lg border border-dashed px-4 py-3">
            <div className="flex items-center gap-3 min-w-0">
              <Upload className="h-5 w-5 shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-sm font-medium">Subir certificado propio</p>
                <p className="text-xs text-muted-foreground">
                  Si ya tenés un certificado de ingresos, podés subirlo directamente.
                </p>
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.webp"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setUploading(true);
                try {
                  await uploadEnterpriseRequestDocument(enterpriseRequestId, nodeId, file);
                  await queryClient.invalidateQueries({ queryKey: ["me", "enterprise-requests"] });
                  toast.success("Certificado subido correctamente");
                } catch {
                  toast.error("Error al subir el archivo");
                } finally {
                  setUploading(false);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }
              }}
            />
            <Button
              variant="outline"
              size="sm"
              className="ml-3 shrink-0"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? (
                <>
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  Subiendo...
                </>
              ) : (
                "Subir archivo"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // State C: Multiple products — selection grid
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{nodeData.label}</CardTitle>
        {nodeData.description && (
          <p className="text-sm text-muted-foreground">
            {nodeData.description}
          </p>
        )}
      </CardHeader>
      <CardContent>
        {products.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("requests.user.internalRequestNoProducts")}
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {products.map(({ intent, catalogItem }) => {
              const amountMinor = catalogItem ? catalogCustomerAmountMinor(catalogItem) : null;
              return (
                <Card
                  key={intent.slug}
                  className="cursor-pointer transition-colors hover:border-primary"
                  onClick={() => navigateToRequest(intent.slug)}
                >
                  <CardContent className="space-y-1 pt-4">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">
                        {catalogItem?.displayName ?? intent.slug}
                      </span>
                    </div>
                    {catalogItem?.description && (
                      <p className="text-xs text-muted-foreground">
                        {catalogItem.description}
                      </p>
                    )}
                    {amountMinor != null && catalogItem && (
                      <p className="text-xs font-medium text-foreground">
                        {formatCatalogAmountMinor(
                          amountMinor,
                          catalogItem.currency,
                          i18n.language,
                        )}
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
